import { useAct } from 'FlowBuilder/store'
import InlineCkEditor from 'Shared/components/CKEditor/InlineCkEditor'
import useActionTranslate from 'FlowBuilder/hooks/useActionTranslate'
import { ActionTranslatableKeysEmailToTarget, actionTranslatablesEmailToTarget } from 'FlowBuilder/lib/actionTranslatable'
import { Label } from 'recharts'

export default function ContentForm({ actionId }) {
  const act = useAct()
  const [userInstructionsSourceMode, setUserInstructionsSourceMode] = useState(false)
  const mergeTags = useSel(s => s.builder.mergeTags)
  const action = useSel(s => s.actions.entities[s.local.selectedActionId])
  const config = action.config || { decisionMakerSetId: '', emailSenderId: '' }
  const decisionMakersSet = useSel(s => s.decisionMakersSet)

  const hideLookupForm = useMemo(() => decisionMakersSet.lookupKind === 'manual', [decisionMakersSet.lookupKind])

  const {
    textContentUpdater,
    translated,
    fallback,
    flag
  } = useActionTranslate({ actionId })

  const { userInstructionsContent } = translated
  const { userInstructionsContent: userInstructionsContentFallback } = fallback

  type FormFieldConfig = Partial<Record<ActionTranslatableKeysEmailToTarget, boolean | (() => boolean)>>

  const showLookupFormFields: FormFieldConfig = {
    'enterYourPostcode': true,
    'enterYourPostcodePlaceholder': true,
    'findMyMp': true,
    'skipButtonText': true,
    'invalidLookupValidationMessage': true,
    'noDecisionMakersFoundMessage': true,
    'otherLookupErrorMessage': true,
  }

  const emailBuilderFormFields: FormFieldConfig = {
    'emailFormHeaderText': true,
    'selectPeopleToEmailText': true,
    'subjectText': true,
    'salutationText': true,
    'emailSignOffText': true,
    'namePlaceholder': true,
    'emailPlaceholder': () => config.requiredUserInfo === 'name_email_postcode_address',
    'addressPlaceholder': () => config.requiredUserInfo === 'name_email_postcode_address',
    'cityPlaceholder': () => config.requiredUserInfo === 'name_email_postcode_address',
    'postcodePlaceholder': () => config.requiredUserInfo === 'name_email_postcode_address',
    'editBodyValidationMessage': () => config.requireBodyEditing === true,
    'sendEmailButtonText': true,
  }

  const translatableFieldInputs = (fieldKeysConfig: FormFieldConfig) => {
    return Object.keys(fieldKeysConfig).map((key) => {
      const typedKey = key as ActionTranslatableKeysEmailToTarget
      const isVisible = fieldKeysConfig[typedKey]
      const { label } = actionTranslatablesEmailToTarget[typedKey]
      if (!isVisible) return null
      if (typeof isVisible === 'function' && !isVisible()) return null
      return <div className='field margin-bottom half'>
        <label className="bold">{label} {flag}</label>
        <input
          className="margin-top half full-width"
          type="text"
          value={translated[typedKey] ?? ''}
          placeholder={fallback[typedKey]}
          onChange={e => textContentUpdater(typedKey, e.target.value)}
        />
      </div>
    })
  }

  return <div>
    <p>Optionally, you can customise the messages shown in this action.</p>
    {!hideLookupForm && <div>
      <h3>Find your representative</h3>
      {translatableFieldInputs(showLookupFormFields)}
    </div>}
    <h3>Send email</h3>
    {translatableFieldInputs(emailBuilderFormFields)}
    {config.includeUserInstructions && <div className='field margin-top half'>
      <label className="bold">User instructions {flag}</label>
      <InlineCkEditor
        value={userInstructionsContent ?? ''}
        placeholder={userInstructionsContentFallback}
        onUpdate={value => textContentUpdater('userInstructionsContent', value)}
        sourceMode={userInstructionsSourceMode}
        setSourceMode={setUserInstructionsSourceMode}
        mergeTags={mergeTags}
        hideMergeTags={true}
        disabled={!config.includeUserInstructions}
      />
    </div>}
  </div>
}
